import { createContext, useContext, useEffect, useRef } from 'react';

// Shared state passed down to every widget by LayoutRenderer.
// Widgets read it via useLayoutCtx() instead of prop drilling. Mirrors the
// `ctx` argument the L4 Script Widget receives, so the same mental model
// applies to JS-authored widgets and React widgets.
//
// Shape:
//   {
//     // identity
//     token, currentUser, workspaceId, role,
//     // category-specific live data (typed nullable)
//     case:        Case | null,
//     workItem:    WorkItem | null,
//     draft:       object | null,        // in-progress field state for add_case / add_task
//     setDraft:    (next) => void,       // patch the in-progress draft
//     // event bus
//     bus:         { publish, subscribe, ... },
//     // host-injected callbacks (varies by category — opaque to widgets)
//     callbacks:   { onCaseUpdated?, onOpenWorkItem?, onCompleted?, onDirtyChange?, onClose?, onCreated?, ... },
//     // L4a wire executor support
//     registerActions: (widgetId, handlersRef) => unregister(),
//     invokeAction:    (widgetId, action, payload) => result,
//   }
const LayoutContext = createContext(null);

export const LayoutContextProvider = LayoutContext.Provider;

export function useLayoutCtx() {
  const v = useContext(LayoutContext);
  if (!v) {
    throw new Error('useLayoutCtx() called outside <LayoutRenderer>');
  }
  return v;
}

/**
 * Per-widget action registration (Phase L4a). Widgets call this with a map of
 * `{actionName: handler}` to expose imperative entry points the wire executor
 * can target. Internally we register a ref to the live handlers map, so the
 * wire executor always invokes the latest handler — no re-register churn on
 * every render.
 *
 * Action `name` must match the same identifier shape the backend wire
 * validator enforces (`^[A-Za-z][A-Za-z0-9_\\-.]*$`). Names declared in
 * `registerWidget({ actions: [...] })` are the surface the Designer's Events
 * panel exposes — anything not in that list still works at runtime but won't
 * be pickable from the dropdown.
 *
 * On unmount we unregister so a stale handler can't fire against a dead widget.
 */
export function useWidgetActions(widgetId, handlers) {
  const ctx = useLayoutCtx();
  const handlersRef = useRef(handlers);
  // Always point the ref at the current handlers map so invokeAction
  // dispatches to the latest closure even between effect runs.
  handlersRef.current = handlers;

  useEffect(() => {
    if (!widgetId || !ctx?.registerActions) return undefined;
    return ctx.registerActions(widgetId, handlersRef);
    // ctx is stable (memoized in the renderer); widgetId is stable per widget.
  }, [widgetId, ctx]);
}

export default LayoutContext;
